/**
 * Images are loaded once and drawn every frame. Without a cache the renderer
 * would build a new `Image` sixty times a second and draw nothing, because
 * none of them would have decoded yet.
 */
export class ImageCache {
  private readonly images = new Map<string, HTMLImageElement>();
  private readonly failed = new Set<string>();

  constructor(private readonly onLoad: () => void) {}

  /** The decoded image, or `undefined` while it loads or after it failed. */
  get(url: string): HTMLImageElement | undefined {
    if (!url || this.failed.has(url)) {
      return undefined;
    }

    const existing = this.images.get(url);
    if (existing) {
      return existing.complete && existing.naturalWidth > 0 ? existing : undefined;
    }

    const image = new Image();
    // Assets may be served from another origin; without this the canvas is
    // tainted and Egress cannot capture it at all.
    image.crossOrigin = 'anonymous';
    image.onload = () => this.onLoad();
    image.onerror = () => {
      this.failed.add(url);
      this.images.delete(url);
    };
    image.src = url;
    this.images.set(url, image);
    return undefined;
  }

  clear(): void {
    this.images.clear();
    this.failed.clear();
  }
}
